import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { useDispatch, useSelector } from 'react-redux';
import { setLoader, setErrorAlert } from '../../store/alert/alertSlice';
import { getConnectionStatus } from '../../utils/connectors/connectorsService';
import { getDataAPI } from '../../utils/fetchData';
import { getUploadedFilesCount } from '../../utils/uploadedFilesTracker';
import { ReactComponent as BandsintownLogo } from '../../assets/images/components/bandsintown-svgrepo-com.svg';
import { ReactComponent as GoogleLogo } from '../../assets/svg/Google__G__logo.svg';
import AppOnboarding from '../../components/AppOnboarding';

const ConnectorsApp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const bandsintownSettings = useSelector(state => state.bandsintown.settings);
  const cachedBookings = useSelector((state) => state.dataCache?.bookings?.data);
  const [activeTab, setActiveTab] = useState('sync');
  const [bookingCount, setBookingCount] = useState(0);
  const [labelDataCount, setLabelDataCount] = useState(0);
  const [loadingData, setLoadingData] = useState(false);
  
  // Initialize connector statuses from localStorage to avoid loading state
  const [connectorStatuses, setConnectorStatuses] = useState(() => {
    try {
      const savedStatuses = localStorage.getItem('connectorStatuses');
      if (savedStatuses) {
        return JSON.parse(savedStatuses);
      }
    } catch (error) {
      console.error('Error loading saved connector statuses:', error);
    }
    return {
      google: false,
      bandsintown: false,
      spotify: true // All accounts should have Spotify connected
    };
  });
  const [loading, setLoading] = useState(false); // Don't show loading by default

  // Available connectors - easily extensible for future integrations
  const connectors = [
    {
      id: 'google',
      name: 'Google',
      color: '#FF2300',
      description: 'Connect and sync with Google services',
      route: '/connectors/google-sheets',
      enabled: true
    },
    {
      id: 'bandsintown',
      name: 'Bandsintown',
      color: '#00CEC8',
      description: 'Sync tour dates automatically',
      route: '/connectors/bandsintown',
      enabled: true,
      comingSoon: false,
    },
    {
      id: 'spotify',
      name: 'Spotify',
      color: '#1DB954',
      description: 'Music catalog, analytics, and playlist management',
      route: '/connectors/spotify',
      enabled: true,
    },
  ];

  const loadConnectorStatuses = useCallback(async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoading(true);
        dispatch(setLoader(true));
      }
      
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      
      // Check connection status for each enabled connector
      const statuses = {
        google: false,
        bandsintown: false,
        spotify: false
      };
      
      // Google integration status check (consolidated Calendar + Gmail)
      try {
        // Check using 'googleCalendar' for now (backend may still use this identifier)
        // Once backend is updated to use 'google', this can be changed
        const googleStatus = await getConnectionStatus('googleCalendar', token);
        statuses.google = googleStatus.connected;
      } catch (error) {
        console.error('Error checking Google integration status:', error);
        statuses.google = false;
      }

      // Bandsintown status check - use Redux state as primary source
      try {
        if (bandsintownSettings && bandsintownSettings.apiKey && bandsintownSettings.artistName) {
          statuses.bandsintown = true;
        } else {
          // Fallback to localStorage if Redux state is not available
          const localBandsintownSettings = localStorage.getItem('bandsintownSettings');
          if (localBandsintownSettings) {
            const settings = JSON.parse(localBandsintownSettings);
            statuses.bandsintown = !!(settings.apiKey && settings.artistName);
          } else {
            statuses.bandsintown = false;
          }
        }
      } catch (error) {
        console.error('Error checking Bandsintown status:', error);
        statuses.bandsintown = false;
      }

      // Spotify status check - all accounts should have Spotify connected
      try {
        // Check spotifyArtistsAgent if available
        try {
          const spotifyArtistsAgent = require('../../services/spotifyArtistsAgent').default;
          if (spotifyArtistsAgent) {
            const spotifyStatus = spotifyArtistsAgent.getStatus();
            if (spotifyStatus && spotifyStatus.isAuthenticated) {
              statuses.spotify = true;
            } else {
              // Default to connected since all accounts should have it
              statuses.spotify = true;
            }
          } else {
            // Default to connected since all accounts should have it
            statuses.spotify = true;
          }
        } catch (e) {
          // Default to connected since all accounts should have it
          statuses.spotify = true;
        }
      } catch (error) {
        console.error('Error checking Spotify status:', error);
        // Default to connected since all accounts should have it
        statuses.spotify = true;
      }
      
      setConnectorStatuses(statuses);
      
      // Save to localStorage for persistence
      try {
        localStorage.setItem('connectorStatuses', JSON.stringify(statuses));
      } catch (error) {
        console.error('Error saving connector statuses to localStorage:', error);
      }
      
    } catch (error) {
      console.error('Error loading connector statuses:', error);
      dispatch(setErrorAlert('Failed to load connector statuses'));
    } finally {
      if (showLoading) {
        setLoading(false);
        dispatch(setLoader(false));
      }
    }
  }, [bandsintownSettings, dispatch]); // Remove connectorStatuses from dependencies

  useEffect(() => {
    // Load statuses silently on initial mount since we have localStorage data
    loadConnectorStatuses(false);
  }, [loadConnectorStatuses]);

  // Direct update for Bandsintown status when settings change
  useEffect(() => {
    if (bandsintownSettings) {
      const isConnected = !!(bandsintownSettings.apiKey && bandsintownSettings.artistName);
      setConnectorStatuses(prev => {
        const newStatuses = {
          ...prev,
          bandsintown: isConnected
        };
        
        // Save to localStorage immediately
        try {
          localStorage.setItem('connectorStatuses', JSON.stringify(newStatuses));
        } catch (error) {
          console.error('Error saving connector statuses to localStorage:', error);
        }
        
        return newStatuses;
      });
    }
  }, [bandsintownSettings]); // Remove connectorStatuses to avoid infinite loop

  // Initialize Spotify as connected (all accounts should have it)
  useEffect(() => {
    setConnectorStatuses(prev => {
      if (prev.spotify === false || prev.spotify === undefined) {
        const newStatuses = {
          ...prev,
          spotify: true // All accounts should have Spotify connected
        };
        
        // Save to localStorage immediately
        try {
          localStorage.setItem('connectorStatuses', JSON.stringify(newStatuses));
        } catch (error) {
          console.error('Error saving connector statuses to localStorage:', error);
        }
        
        return newStatuses;
      }
      return prev;
    });
  }, []);

  const handleConnectorClick = (connector) => {
    if (!connector.enabled) {
      return; // Coming soon connectors are not clickable
    }
    navigate(connector.route);
  };

  const loadDataCounts = useCallback(async () => {
    setLoadingData(true);
    try {
      // Use the utility function to get counts
      const bookingCount = getUploadedFilesCount('booking');
      const labelCount = getUploadedFilesCount('label');
      
      setBookingCount(bookingCount);
      setLabelDataCount(labelCount);
      
      // Also try API to sync if available
      try {
        const token = JSON.parse(localStorage.getItem('dizeeToken'));
        const filesResponse = await getDataAPI('user/get-uploaded-files', token);
        if (filesResponse.data.status === 200 && filesResponse.data.data) {
          const files = Array.isArray(filesResponse.data.data) 
            ? filesResponse.data.data 
            : filesResponse.data.data.files || [];
          
          const bookingFiles = files.filter(f => f.dataType === 'booking' || f.type === 'booking');
          const labelFiles = files.filter(f => f.dataType === 'label' || f.type === 'label');
          
          setBookingCount(bookingFiles.length);
          setLabelDataCount(labelFiles.length);
        }
      } catch (error) {
        // API not available, use localStorage counts
        console.warn('Upload files API not available, using localStorage:', error);
      }
    } catch (error) {
      console.error('Error loading data counts:', error);
    } finally {
      setLoadingData(false);
    }
  }, []);

  // Load data counts when Data tab is active
  useEffect(() => {
    if (activeTab === 'data') {
      loadDataCounts();
    }
  }, [activeTab, loadDataCounts]);

  const handleBookingUpload = () => {
    navigate('/connectors/data-upload', { state: { type: 'booking' } });
  };

  const handleBookingView = () => {
    navigate('/connectors/data-view', { state: { category: 'bookings' } });
  };

  const handleLabelUpload = () => {
    navigate('/connectors/data-upload', { state: { type: 'label' } });
  };

  const handleLabelView = () => {
    navigate('/connectors/data-view', { state: { category: 'label' } });
  };

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Data</span>
            <span 
              className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={() => {
                  const canGoBack = window.history.length > 1;
                  if (canGoBack) {
                    navigate(-1);
                  } else {
                    navigate('/');
                  }
                }}
            >
              Go back
            </span>
          </div>

          {/* Tabs */}
          <div className="w-full mb-6 px-4">
            <div className="flex w-full">
              <button
                onClick={() => setActiveTab('sync')}
                className={`flex-1 py-2 text-[12px] transition-colors relative ${
                  activeTab === 'sync' 
                    ? 'text-white' 
                    : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                }`}
              >
                Sync
                {activeTab === 'sync' && (
                  <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#FF2300]"></div>
                )}
              </button>
              <button
                onClick={() => setActiveTab('data')}
                className={`flex-1 py-2 text-[12px] transition-colors relative ${
                  activeTab === 'data' 
                    ? 'text-white' 
                    : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                }`}
              >
                Data
                {activeTab === 'data' && (
                  <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#FF2300]"></div>
                )}
              </button>
            </div>
          </div>

          <div className="w-full px-4 pb-20 overflow-y-auto flex-1">
            {activeTab === 'sync' && (
              <>
                {/* Onboarding - shows when no connectors connected */}
                {!connectorStatuses.google && !connectorStatuses.bandsintown && (
                  <div className="mb-6">
                    <AppOnboarding
                      appName="Connectors"
                      description="Connect your platforms to sync data automatically and keep everything up to date."
                      color="#FF2300"
                      steps={[
                        {
                          id: 'connect-google',
                          title: 'Connect Google',
                          description: 'Sync calendar, sheets & contacts',
                          completed: connectorStatuses.google,
                          onClick: () => navigate('/connectors/google')
                        },
                        {
                          id: 'connect-bandsintown',
                          title: 'Connect Bandsintown',
                          description: 'Auto-import tour dates',
                          completed: connectorStatuses.bandsintown,
                          onClick: () => navigate('/connectors/bandsintown')
                        },
                        {
                          id: 'connect-spotify',
                          title: 'Connect Spotify',
                          description: 'Sync your music catalog',
                          completed: connectorStatuses.spotify,
                          onClick: () => navigate('/connectors/spotify')
                        }
                      ]}
                      actionText="Connect Google"
                      onAction={() => navigate('/connectors/google')}
                      icon={
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                      }
                    />
                  </div>
                )}
                
                {/* Description */}
                <div className="mb-8">
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                    Connect your favorite platforms to Dizee. Sync your calendar, track analytics, 
                    and manage everything from one place.
                  </p>
                </div>

                {/* Connectors Grid */}
                <div className="space-y-4">
                  {connectors.map((connector) => {
                    const isConnected = connectorStatuses[connector.id];
                    const isDisabled = !connector.enabled;

                    return (
                      <div
                        key={connector.id}
                        className={`
                          w-full bg-black border border-[rgba(255,255,255,0.1)] rounded-lg
                          transition-all relative overflow-hidden
                          ${!isDisabled ? '' : 'opacity-50'}
                        `}
                      >
                        {/* For Google, show header and tabs instead of clickable card */}
                        {connector.id === 'google' ? (
                          <>
                            {/* Google Header (non-clickable) */}
                            <div className="p-4 border-b border-[rgba(255,255,255,0.1)]">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4 flex-1">
                                  {/* Icon */}
                                  <div 
                                    className="w-12 h-12 rounded-lg flex items-center justify-center border border-[rgba(255,255,255,0.1)]"
                                    style={{ backgroundColor: `${connector.color}20` }}
                                  >
                                    <GoogleLogo className="w-6 h-6" />
                                  </div>

                                  {/* Info */}
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <p className="text-white text-[12px] font-medium">
                                        {connector.name}
                                      </p>
                                    </div>
                                    <p className="text-white text-[11px] opacity-50">
                                      {connector.description}
                                    </p>
                                  </div>
                                </div>

                                {/* Status Badge */}
                                {isConnected && (
                                  <div className="ml-3">
                                    <div className="flex items-center gap-1.5 bg-green-500 bg-opacity-20 border border-green-500 border-opacity-30 rounded-full px-3 py-1.5">
                                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                                      <span className="text-green-500 text-[10px] font-medium">
                                        Connected
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Google Tabs */}
                            <div className="p-2">
                              {/* Google Sheets Tab */}
                              <div 
                                onClick={() => navigate('/connectors/google-sheets')}
                                className="p-3 rounded-lg flex items-center justify-between cursor-pointer hover:bg-[rgba(255,255,255,0.05)] transition-all mb-2"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-lg flex items-center justify-center border border-[rgba(255,255,255,0.1)] bg-[rgba(0,206,209,0.1)]">
                                    <svg className="w-4 h-4 text-[#00CED1]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                  </div>
                                  <div>
                                    <p className="text-white text-[11px] font-medium">Google Sheets</p>
                                    <p className="text-white text-[10px] opacity-50">Sync bookings and catalog data</p>
                                  </div>
                                </div>
                                <svg className="w-4 h-4 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </div>

                              {/* Google Sync Tab */}
                              <div 
                                onClick={() => navigate('/connectors/google')}
                                className="p-3 rounded-lg flex items-center justify-between cursor-pointer hover:bg-[rgba(255,255,255,0.05)] transition-all"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-lg flex items-center justify-center border border-[rgba(255,255,255,0.1)] bg-[rgba(0,206,209,0.1)]">
                                    <svg className="w-4 h-4 text-[#00CED1]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                  </div>
                                  <div>
                                    <p className="text-white text-[11px] font-medium">Google Sync</p>
                                    <p className="text-white text-[10px] opacity-50">Calendar sync and email tracking</p>
                                  </div>
                                </div>
                                <svg className="w-4 h-4 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </div>
                            </div>
                          </>
                        ) : (
                          /* Regular connector card (clickable) */
                          <div
                            onClick={() => handleConnectorClick(connector)}
                            className={`
                              p-4
                              ${!isDisabled ? 'hover:border-[rgba(255,255,255,0.3)] cursor-pointer' : 'cursor-not-allowed'}
                        `}
                      >
                        <div className="relative z-10 flex items-center justify-between">
                          <div className="flex items-center gap-4 flex-1">
                            {/* Icon */}
                            <div 
                              className="w-12 h-12 rounded-lg flex items-center justify-center border border-[rgba(255,255,255,0.1)]"
                              style={{ backgroundColor: connector.enabled ? `${connector.color}20` : 'rgba(255,255,255,0.05)' }}
                            >
                              {connector.enabled && (
                                <>
                                      {connector.id === 'bandsintown' ? (
                                    <BandsintownLogo className="w-6 h-6" style={{ fill: connector.color }} />
                                  ) : connector.id === 'spotify' ? (
                                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill={connector.color}>
                                      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/>
                                    </svg>
                                  ) : (
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: connector.color }}>
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                  )}
                                </>
                              )}
                            </div>

                            {/* Info */}
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="text-white text-[12px] font-medium">
                                  {connector.name}
                                </p>
                              </div>
                              <p className="text-white text-[11px] opacity-50">
                                {connector.description}
                              </p>
                            </div>
                          </div>

                          {/* Status Badge */}
                          {connector.enabled && (
                            <div className="ml-3">
                              {isConnected ? (
                                <div className="flex items-center gap-1.5 bg-green-500 bg-opacity-20 border border-green-500 border-opacity-30 rounded-full px-3 py-1.5">
                                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                                  <span className="text-green-500 text-[10px] font-medium">
                                    Connected
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1.5 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-full px-3 py-1.5">
                                  <div className="w-2 h-2 bg-white opacity-30 rounded-full" />
                                  <span className="text-white text-[10px] opacity-50">
                                    Not Connected
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {activeTab === 'data' && (
              <>
                {/* Description */}
                <div className="mb-8">
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                    Upload files and documents so Dizee can map them to the right apps.
                  </p>
                </div>

                {/* IMPORT BOOKINGS Section */}
                <div className="mb-6 bg-black border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white text-[10px] opacity-50">IMPORT BOOKINGS</p>
                    {bookingCount > 0 && (
                      <p className="text-white text-[10px] opacity-70">
                        {bookingCount} {bookingCount === 1 ? 'file' : 'files'}
                      </p>
                    )}
                  </div>
                  <h3 className="text-white text-[14px] font-semibold mb-2">Upload Touring Data</h3>
                  <p className="text-white text-[11px] opacity-70 mb-4">
                    Upload booking agency csv files from Wasserman, CAA, UTA and WME.
                  </p>
                  <div className="flex gap-2 mb-4">
                    <button
                      onClick={handleBookingView}
                      className="flex-1 bg-transparent border border-[rgba(255,255,255,0.2)] text-white text-[12px] font-semibold py-2 rounded-lg hover:bg-[rgba(255,255,255,0.05)] transition-all"
                    >
                      View
                    </button>
                    <button
                      onClick={handleBookingUpload}
                      className="flex-1 bg-[#FF2300] text-white text-[12px] font-semibold py-2 rounded-lg hover:opacity-90 transition-all"
                    >
                      Upload
                    </button>
                  </div>
                  <p className="text-white text-[10px] opacity-50">
                    Apps Loaded. Bookings. Ticket Tracker, TourMngr
                  </p>
                </div>

                {/* LABEL & DISTRIBUTOR Section */}
                <div className="mb-6 bg-black border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white text-[10px] opacity-50">LABEL & DISTRIBUTOR</p>
                    {labelDataCount > 0 && (
                      <p className="text-white text-[10px] opacity-70">
                        {labelDataCount} {labelDataCount === 1 ? 'file' : 'files'}
                      </p>
                    )}
                  </div>
                  <h3 className="text-white text-[14px] font-semibold mb-2">Upload Songwriter Splits & Info</h3>
                  <p className="text-white text-[11px] opacity-70 mb-4">
                    Upload PRO csv files from ASCAP, BMI, SESAC and SOCAN.
                  </p>
                  <div className="flex gap-2 mb-4">
                    <button
                      onClick={handleLabelView}
                      className="flex-1 bg-transparent border border-[rgba(255,255,255,0.2)] text-white text-[12px] font-semibold py-2 rounded-lg hover:bg-[rgba(255,255,255,0.05)] transition-all"
                    >
                      View
                    </button>
                    <button
                      onClick={handleLabelUpload}
                      className="flex-1 bg-[#FF2300] text-white text-[12px] font-semibold py-2 rounded-lg hover:opacity-90 transition-all"
                    >
                      Upload
                    </button>
                  </div>
                  <p className="text-white text-[10px] opacity-50">
                    Apps Loaded. Money. Catalog, Analytics
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default ConnectorsApp;

